import React, { useState, useEffect } from 'react';
import { X, Activity, Zap, Radio, Cloud, Info } from 'lucide-react';
import { getModuleDisplayName } from '../../utils/moduleNames';
import { beaconsAPI } from '../../services/api';

const ScanModal = ({ isOpen, onClose, onSave, targets = [], modules = [] }) => {
  const [formData, setFormData] = useState({
    target_id: '',
    module_name: '',
    scan_type: 'passive'
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [cloudLoading, setCloudLoading] = useState(false);
  const [cloudQuota, setCloudQuota] = useState(null);
  const [hasOwnBeacon, setHasOwnBeacon] = useState(true);
  const [showCloudTooltip, setShowCloudTooltip] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        target_id: targets[0]?.id || '',
        module_name: '',
        scan_type: 'passive'
      });
      setErrors({});
      // Check cloud quota and own beacons
      beaconsAPI.getCloudQuota().then(res => setCloudQuota(res.data)).catch(() => {});
      beaconsAPI.list(0, 100).then(res => {
        const own = (res.data.beacons || []).some(
          b => b.profile === 'pentester' && b.status === 'online' && !(b.tags || []).includes('pool:shared')
        );
        setHasOwnBeacon(own);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If target changes, reset module selection
    if (name === 'target_id') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        module_name: ''  // Reset to no selection when target changes
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.target_id) {
      newErrors.target_id = 'Target is required';
    }
    
    if (!formData.module_name) {
      newErrors.module_name = 'Module is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e, useCloud = false) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    if (useCloud) {
      setCloudLoading(true);
    } else {
      setLoading(true);
    }
    try {
      await onSave({ ...formData, use_cloud: useCloud });
      onClose();
    } catch (error) {
      setErrors({ submit: error.response?.data?.detail || 'Failed to start scan' });
    } finally {
      setLoading(false);
      setCloudLoading(false);
    }
  };

  if (!isOpen) return null;

  // Get selected target to filter compatible modules
  const selectedTarget = targets.find(t => t.id === parseInt(formData.target_id));
  const targetType = selectedTarget?.type?.toUpperCase();

  // Filter modules by scan type AND target compatibility
  const passiveModules = modules.filter(m => {
    if (m.scan_type !== 'passive') return false;
    // If no target selected or module doesn't specify supported types, show all
    if (!targetType || !m.supported_target_types || m.supported_target_types.length === 0) return true;
    // Check if target type is supported (case-insensitive)
    return m.supported_target_types.some(t => t.toUpperCase() === targetType);
  });
  
  const activeModules = modules.filter(m => {
    if (m.scan_type !== 'active') return false;
    // If no target selected or module doesn't specify supported types, show all
    if (!targetType || !m.supported_target_types || m.supported_target_types.length === 0) return true;
    // Check if target type is supported (case-insensitive)
    return m.supported_target_types.some(t => t.toUpperCase() === targetType);
  });

  const selectedModule = modules.find(m => m.name === formData.module_name);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="cyber-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-shinobi-gradient flex items-center justify-center">
              <Activity className="w-5 h-5 text-cyber-50" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-shinobi-purple">Start New Scan</h2>
              <p className="text-sm text-slate-500">
                Execute a scan module on a target
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form id="scan-form" onSubmit={handleSubmit} className="p-4 space-y-4">
          {errors.submit && (
            <div className="bg-red-900/20 border border-danger-red text-danger-red px-4 py-3 rounded text-sm">
              {errors.submit}
            </div>
          )}

          {/* Target Selection */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Target <span className="text-danger-red">*</span>
            </label>
            <select
              name="target_id"
              value={formData.target_id}
              onChange={handleChange}
              className={`terminal-input w-full px-3 py-2 rounded focus:ring-2 focus:ring-shinobi-purple text-sm ${
                errors.target_id ? 'border-danger-red' : ''
              }`}
              required
            >
              <option value="">Select target...</option>
              {targets.map(target => (
                <option key={target.id} value={target.id}>
                  {target.name || target.value} ({target.type})
                </option>
              ))}
            </select>
            {errors.target_id && (
              <p className="text-danger-red text-sm mt-1">{errors.target_id}</p>
            )}
          </div>

          {/* Scan Type */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Scan Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  if (formData.scan_type !== 'passive') {
                    setFormData(prev => ({ ...prev, scan_type: 'passive', module_name: '' }));
                  }
                }}
                className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                  formData.scan_type === 'passive'
                    ? 'border-shinobi-purple bg-shinobi-purple/10'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Zap className={`w-6 h-6 mb-1 ${formData.scan_type === 'passive' ? 'text-shinobi-purple' : 'text-slate-500'}`} />
                <span className={`font-semibold text-sm ${formData.scan_type === 'passive' ? 'text-shinobi-purple' : 'text-slate-500'}`}>
                  GhostPulse
                </span>
                <span className="text-xs text-gray-500 mt-1">Passive Scanning</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (formData.scan_type !== 'active') {
                    setFormData(prev => ({ ...prev, scan_type: 'active', module_name: '' }));
                  }
                }}
                className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                  formData.scan_type === 'active'
                    ? 'border-shinobi-orange bg-shinobi-orange/10'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Activity className={`w-6 h-6 mb-1 ${formData.scan_type === 'active' ? 'text-shinobi-orange' : 'text-slate-500'}`} />
                <span className={`font-semibold text-sm ${formData.scan_type === 'active' ? 'text-shinobi-orange' : 'text-slate-500'}`}>
                  NoxScan
                </span>
                <span className="text-xs text-gray-500 mt-1">Active Scanning</span>
              </button>
            </div>
          </div>

          {/* Module Selection */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Scan Module <span className="text-danger-red">*</span>
            </label>
            <select
              name="module_name"
              value={formData.module_name}
              onChange={handleChange}
              className={`terminal-input w-full px-3 py-2 rounded focus:ring-2 focus:ring-shinobi-purple text-sm ${
                errors.module_name ? 'border-danger-red' : ''
              }`}
              required
            >
              <option value="">Select module...</option>
              {(formData.scan_type === 'passive' ? passiveModules : activeModules).map(module => {
                const label = getModuleDisplayName(module.name);
                const avail = module.beacons_available;
                const hasBeacons = avail == null || avail > 0;
                const tag = avail != null
                  ? hasBeacons
                    ? ` [${avail} beacon${avail !== 1 ? 's' : ''}]`
                    : ' — Coming Soon'
                  : '';
                return (
                  <option key={module.name} value={module.name} disabled={!hasBeacons}>
                    {label}{tag}
                  </option>
                );
              })}
            </select>
            {errors.module_name && (
              <p className="text-danger-red text-sm mt-1">{errors.module_name}</p>
            )}
          </div>

          {/* Module Info - Animated */}
          <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
            selectedModule ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}>
            {selectedModule && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 animate-fadeIn">
                <h4 className="text-sm font-semibold text-shinobi-purple mb-3">Module Details</h4>
                <div className="space-y-2 text-sm text-slate-500">
                  <p><span className="text-gray-500">Description:</span> {selectedModule.description}</p>
                  <p><span className="text-gray-500">Version:</span> {selectedModule.version}</p>
                  <p><span className="text-gray-500">Author:</span> {selectedModule.author}</p>
                  <p><span className="text-gray-500">Est. Duration:</span> ~{Math.max(1, Math.floor(selectedModule.estimated_duration / 60))} minutes</p>
                  {selectedModule.beacons_available != null && (
                    <div className="flex items-center space-x-2 mt-1">
                      <Radio className={`w-3.5 h-3.5 ${selectedModule.beacons_available > 0 ? 'text-neon-green' : 'text-yellow-600'}`} />
                      <span className={selectedModule.beacons_available > 0 ? 'text-neon-green' : 'text-yellow-600'}>
                        {selectedModule.beacons_available > 0
                          ? `${selectedModule.beacons_available} beacon${selectedModule.beacons_available !== 1 ? 's' : ''} available`
                          : 'No beacons online — scan will queue until a beacon connects'}
                      </span>
                    </div>
                  )}
                  {selectedModule.requires_api_key && (
                    <p className="text-yellow-600">Requires API key configuration</p>
                  )}
                </div>
              </div>
            )}
          </div>

        </form>

        {/* Actions */}
        <div className="p-4 border-t border-slate-200 space-y-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors text-sm"
              disabled={loading || cloudLoading}
            >
              Cancel
            </button>

            {/* Primary action — depends on whether user has own beacon */}
            {hasOwnBeacon ? (
              <button
                type="submit"
                form="scan-form"
                className="btn-cyber px-5 py-2 rounded flex items-center space-x-2 text-sm"
                disabled={loading || cloudLoading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Starting...</span>
                  </>
                ) : (
                  <>
                    <Activity className="w-4 h-4" />
                    <span>Start Scan</span>
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={(e) => handleSubmit(e, true)}
                disabled={cloudLoading || loading || !cloudQuota?.allowed}
                className="px-5 py-2 rounded flex items-center space-x-2 text-sm bg-neon-blue/20 text-neon-blue border border-neon-blue/30 hover:bg-neon-blue/30 transition-colors disabled:opacity-40"
              >
                {cloudLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-neon-blue"></div>
                    <span>Starting...</span>
                  </>
                ) : (
                  <>
                    <Cloud className="w-4 h-4" />
                    <span>Run on Cloud</span>
                  </>
                )}
              </button>
            )}
          </div>

          {/* Secondary option */}
          {hasOwnBeacon ? (
            /* User has own beacon — show "or run on cloud" as subtle link */
            <div className="flex items-center justify-end space-x-2">
              <button
                type="button"
                onClick={(e) => handleSubmit(e, true)}
                disabled={cloudLoading || loading || !cloudQuota?.allowed}
                className="text-xs text-gray-500 hover:text-neon-blue transition-colors flex items-center space-x-1 disabled:opacity-30"
              >
                <Cloud className="w-3 h-3" />
                <span>or run on cloud</span>
              </button>
              {cloudQuota && (
                <span className="text-xs text-gray-700">({cloudQuota.used_this_month}/{cloudQuota.max_per_month} used)</span>
              )}
            </div>
          ) : (
            /* No own beacon — explain */
            <div className="flex items-center justify-end space-x-1.5 text-xs text-gray-600">
              <Info className="w-3 h-3" />
              <span>No own beacon online. Scan runs on Wasabi Shinobi Cloud.</span>
              {cloudQuota && (
                <span className="text-gray-700">({cloudQuota.used_this_month}/{cloudQuota.max_per_month} used)</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScanModal;
